import { useAuth } from '@/lib'
import { parseNoteContent } from '@/lib/utils'
import { ConfirmationDialog, type ConfirmationDialogRef, logger, TimeAgo } from '@/ui'
import { Button } from '@/ui/rapid-ui/button'
import {
	type CandidateNoteData,
	candidateNoteIndexQueryKey,
	type CandidateNoteIndexResponse,
	type MentionableUserData,
	pipelineStageShowQueryKey,
	useCandidateNoteIndex,
	useCandidateNoteMentionableUsers,
	useCandidateNoteStore,
	useCandidateNoteUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowUpIcon, Loader2Icon, PencilIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MentionTextarea } from './mention-textarea'
import { NoteContent } from './note-content'
import { deserializeMentions, mentionedUserIds, type NoteMention, serializeMentions } from './note-mentions'

const MENTIONABLE_USERS_STALE_TIME = 5 * 60 * 1000

interface CandidateInternalNotesProps {
	candidateId: string
	candidateName: string
	campaignId: string
}

interface NoteDraft {
	text: string
	mentions: NoteMention[]
	original: string
}

interface NoteItemProps {
	note: CandidateNoteData
	candidateId: string
	canEdit: boolean
	mentionableUsers: MentionableUserData[]
	onMentionsRequested: () => void
	confirmDiscard: () => Promise<boolean>
}

function NoteItem(
	{ note, candidateId, canEdit, mentionableUsers, onMentionsRequested, confirmDiscard }: NoteItemProps,
) {
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const [draft, setDraft] = useState<NoteDraft | null>(null)

	const updateMutation = useCandidateNoteUpdate({
		onSuccess: (updatedNote, variables) => {
			queryClient.setQueryData<CandidateNoteIndexResponse>(
				candidateNoteIndexQueryKey(candidateId),
				(previous) =>
					previous && {
						...previous,
						data: previous.data.map((existing) => existing.id === updatedNote.id ? updatedNote : existing),
					},
			)
			void queryClient.invalidateQueries({ queryKey: candidateNoteIndexQueryKey(candidateId) })
			logger.track('Candidates: Note edited', {
				data: {
					candidate_id: candidateId,
					mention_count: mentionedUserIds(variables.data.content).length,
				},
			})
			setDraft(null)
		},
	})

	const isDirty = draft !== null && draft.text !== draft.original
	const canSave = draft !== null && isDirty && draft.text.trim() !== '' && !updateMutation.isPending

	const startEditing = () => {
		const { text, mentions } = deserializeMentions(parseNoteContent(note.content) ?? '')
		updateMutation.reset()
		setDraft({ text, mentions, original: text })
	}

	const save = () => {
		if (!draft || !canSave) {
			return
		}
		updateMutation.mutate({
			candidate: candidateId,
			note: note.id,
			data: { content: serializeMentions(draft.text.trim(), draft.mentions) },
		})
	}

	const cancelEditing = async () => {
		if (isDirty && !(await confirmDiscard())) {
			return
		}
		updateMutation.reset()
		setDraft(null)
	}

	return (
		<div className="bg-white border border-gray-200 rounded-lg p-4">
			<div className="flex items-center justify-between gap-2 mb-2">
				<span className="text-sm font-semibold text-gray-900">
					{note.userName ?? note.userEmail ?? t('notes.unknownUser')}
				</span>
				<span className="flex items-center gap-2 text-sm text-gray-400">
					<TimeAgo date={new Date(note.createdAt)} />
					{note.editedAt && <span>{t('notes.edited')}</span>}
					{canEdit && draft === null && (
						<Button type="invisible" size="tiny" onClick={startEditing} ariaLabel={t('notes.edit')}>
							<PencilIcon className="size-3.5" />
						</Button>
					)}
				</span>
			</div>
			{draft === null
				? <NoteContent content={note.content} />
				: (
					<div className="space-y-2">
						<MentionTextarea
							value={draft.text}
							onChange={(text) => setDraft((current) => current && { ...current, text })}
							mentions={draft.mentions}
							onMentionsChange={(mentions) => setDraft((current) => current && { ...current, mentions })}
							users={mentionableUsers}
							placement="below"
							onFocus={onMentionsRequested}
							onKeyDown={(event) => {
								if (event.key === 'Enter' && !event.shiftKey) {
									event.preventDefault()
									save()
								}
								if (event.key === 'Escape') {
									event.preventDefault()
									void cancelEditing()
								}
							}}
							disabled={updateMutation.isPending}
							autoFocus
							rows={2}
							className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none field-sizing-content disabled:opacity-60"
						/>
						{updateMutation.isError && (
							<p role="alert" className="text-sm text-red-600">{t('notes.saveError')}</p>
						)}
						<div className="flex justify-end gap-2">
							<Button
								type="invisible"
								size="tiny"
								onClick={() => void cancelEditing()}
								disabled={updateMutation.isPending}
							>
								{t('common.cancel')}
							</Button>
							<Button
								type="committal"
								size="inline"
								onClick={save}
								disabled={!canSave}
								isLoading={updateMutation.isPending}
							>
								{t('common.save')}
							</Button>
						</div>
					</div>
				)}
		</div>
	)
}

export function CandidateInternalNotes({ candidateId, candidateName, campaignId }: CandidateInternalNotesProps) {
	const { t } = useTranslation('common')
	const { user } = useAuth()
	const queryClient = useQueryClient()
	const [newNote, setNewNote] = useState('')
	const [newNoteMentions, setNewNoteMentions] = useState<NoteMention[]>([])
	const [mentionsRequested, setMentionsRequested] = useState(false)
	const hasTrackedInput = useRef(false)
	const notesContainerRef = useRef<HTMLDivElement>(null)
	const confirmRef = useRef<ConfirmationDialogRef>(null)

	const notesQuery = useCandidateNoteIndex(candidateId, {
		enabled: Boolean(candidateId),
	})

	const mentionableUsersQuery = useCandidateNoteMentionableUsers(candidateId, {
		enabled: Boolean(candidateId) && mentionsRequested,
		staleTime: MENTIONABLE_USERS_STALE_TIME,
	})
	const mentionableUsers = mentionableUsersQuery.data ?? []
	const requestMentionableUsers = useCallback(() => setMentionsRequested(true), [])

	const storeMutation = useCandidateNoteStore({
		onSuccess: () => {
			setNewNote('')
			setNewNoteMentions([])
			hasTrackedInput.current = false
			void queryClient.invalidateQueries({ queryKey: candidateNoteIndexQueryKey(candidateId) })
			void queryClient.invalidateQueries({ queryKey: pipelineStageShowQueryKey(campaignId) })
		},
	})

	const handleSubmit = useCallback(() => {
		const trimmed = newNote.trim()
		if (!trimmed || storeMutation.isPending) { return }

		storeMutation.mutate({ candidate: candidateId, data: { content: serializeMentions(trimmed, newNoteMentions) } })
	}, [candidateId, newNote, newNoteMentions, storeMutation])

	const handleNoteChange = (value: string) => {
		if (!hasTrackedInput.current && value.trim()) {
			hasTrackedInput.current = true
			logger.track('Candidates: Note input started', {
				data: {
					candidate_id: candidateId,
				},
			})
		}
		setNewNote(value)
	}

	const confirmDiscard = useCallback(async () => {
		try {
			await confirmRef.current?.confirm({
				title: t('notes.discardTitle'),
				message: t('notes.discardMessage'),
				confirmText: t('notes.discardConfirm'),
				cancelText: t('common.cancel'),
				destructive: true,
			})
			return true
		} catch {
			// cancelled
			return false
		}
	}, [t])

	const notes = notesQuery.data?.data ?? []
	const firstName = candidateName.split(' ')[0]

	useEffect(() => {
		if (notesContainerRef.current) {
			notesContainerRef.current.scrollTop = notesContainerRef.current.scrollHeight
		}
	}, [notes.length])

	return (
		<div className="bg-gray-50 rounded-lg p-4 space-y-4">
			<div className="flex items-center justify-between">
				<h3 className="text-base font-medium text-gray-700">{t('notes.title')}</h3>
				<span className="text-sm text-gray-400">{t('notes.notVisible', { firstName })}</span>
			</div>

			<div ref={notesContainerRef} className="space-y-3 max-h-64 overflow-y-auto">
				{notes.length === 0
					? (
						<p className="text-sm text-gray-400 text-center py-4">
							{t('notes.empty')}
						</p>
					)
					: (
						notes.map((note) => (
							<NoteItem
								key={note.id}
								note={note}
								candidateId={candidateId}
								canEdit={user !== null && note.userId === user.id}
								mentionableUsers={mentionableUsers}
								onMentionsRequested={requestMentionableUsers}
								confirmDiscard={confirmDiscard}
							/>
						))
					)}
			</div>

			<div className="relative flex items-end">
				<MentionTextarea
					value={newNote}
					onChange={handleNoteChange}
					mentions={newNoteMentions}
					onMentionsChange={setNewNoteMentions}
					users={mentionableUsers}
					onFocus={requestMentionableUsers}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && !event.shiftKey) {
							event.preventDefault()
							handleSubmit()
						}
					}}
					placeholder={t('notes.placeholder')}
					rows={1}
					className="w-full px-4 py-3 pr-12 text-sm bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none field-sizing-content"
				/>
				<button
					type="button"
					onClick={handleSubmit}
					disabled={!newNote.trim() || storeMutation.isPending}
					className="absolute right-2 bottom-2 p-2 rounded-full bg-brand-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-brand-700 transition-colors"
				>
					{storeMutation.isPending
						? <Loader2Icon className="size-4 animate-spin" />
						: <ArrowUpIcon className="size-4" />}
				</button>
			</div>

			<ConfirmationDialog ref={confirmRef} />
		</div>
	)
}
